import { useEffect, useCallback, useRef, useState } from 'react';
import { SurgicalProcedureCoverage } from '../../../types';
import apiService from '../../../services/api';

const LIMIT = 20;

export const useInfiniteSurgicalProcedures = () => {
  const [procedures, setProcedures] = useState<SurgicalProcedureCoverage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchProcedures = useCallback(async (page: number, reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: LIMIT,
        isActive: true,
      };

      const response = await apiService.getSurgicalProcedures(params);

      if (reset) {
        setProcedures(response.procedures);
      } else {
        setProcedures(prev => [...prev, ...response.procedures]);
      }

      setTotal(response.pagination.total);
      setCurrentPage(page);
      setHasMore(response.pagination.hasNext || response.procedures.length === LIMIT);
    } catch (err: any) {
      setError(err.message || 'Error al cargar procedimientos quirúrgicos');
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchProcedures(currentPage + 1, false);
    }
  }, [loading, hasMore, currentPage, fetchProcedures]);

  // Track if initial load has been done
  const initialLoadRef = useRef(false);

  // Initial load (only once)
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchProcedures(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    procedures,
    loading,
    error,
    hasMore,
    fetchMore,
    currentPage,
    total,
  };
};
